import React, { useState } from "react";
import { api } from "@/lib/api";
import { usePaywallGuard } from "@/lib/usePaywallGuard";
import { makeKey, withCache, getCache, setCache } from "@/lib/offlineCache";
import OfflineBadge from "@/components/OfflineBadge";
import DatePicker from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PlaceInput from "@/components/PlaceInput";
import { useI18n } from "@/contexts/I18nContext";
import { Sparkles, MapPin, Clock, TrendingUp, Download, AlertTriangle, Trophy, Brain, Loader2, Share2 } from "lucide-react";

const RASI_SHORT = ["Ari", "Tau", "Gem", "Can", "Leo", "Vir", "Lib", "Sco", "Sag", "Cap", "Aqu", "Pis"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DIVISIONAL_TABS = [
  { key: "d2_hora", label: "D2 Hora", desc: "Wealth" },
  { key: "d7_saptamsa", label: "D7 Saptamsa", desc: "Progeny" },
  { key: "d9_navamsa", label: "D9 Navamsa", desc: "Marriage" },
  { key: "d10_dasamsa", label: "D10 Dasamsa", desc: "Career" },
  { key: "d12_dwadasamsa", label: "D12 Dwadasamsa", desc: "Parents" },
];

export default function BirthChart() {
  const { t } = useI18n();
  const guard = usePaywallGuard();
  const [form, setForm] = useState({ dob: "", time: "12:00", name: "" });
  const [city, setCity] = useState(null);
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState("");
  const [cachedAt, setCachedAt] = useState(null);
  // AI interpretation for a specific divisional chart (d1_rashi / d9_navamsa / d10_dasamsa)
  const [divAI, setDivAI] = useState({});    // { d9_navamsa: "text..." }
  const [divAILoading, setDivAILoading] = useState(null); // which key is loading
  // Share Chart Image feature
  const [shareLoading, setShareLoading] = useState(false);
  const [shareImage, setShareImage] = useState(null);     // {image, filename, share_text}

  const downloadPdf = async () => {
    if (!city || !form.dob) return;
    setPdfLoading(true);
    try {
      const resp = await fetch(`${BACKEND}/api/birth-chart/pdf`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name || "Seeker",
          dob: form.dob,
          time: form.time,
          lat: city.lat,
          lon: city.lon,
          tz: city.tz,
        }),
      });
      if (!resp.ok) throw new Error("PDF generation failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cosmic-sage-${(form.name || "report").replace(/\s/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!city) {
      setError("Please search and select your birth city");
      return;
    }
    setLoading(true);
    setChart(null);
    setCachedAt(null);
    try {
      const body = {
        dob: form.dob,
        time: form.time,
        lat: city.lat,
        lon: city.lon,
        tz: city.tz,
        name: form.name || null,
      };
      const cacheKey = makeKey("birth-chart", body);

      // 1) Show cached chart instantly if we already computed this birth chart
      //    (deterministic — same DOB/time/place → same chart forever).
      const cached = getCache(cacheKey);
      if (cached) {
        setChart(cached.data);
        setCachedAt(cached.cachedAt);
      }

      // 2) Try to refresh from server. If offline, keep the cached version.
      try {
        const fresh = await guard(
          async () => (await api.post("/astro/birth-chart", body)).data,
          "Vedic Birth Chart"
        );
        setChart(fresh);
        setCachedAt(null);
        setCache(cacheKey, fresh);
      } catch (netErr) {
        if (!cached) throw netErr;
        // We had a cached copy — silently use it; user already sees it.
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDivisionalAI = async (which) => {
    if (!city || !form.dob) return;
    if (divAI[which] || divAILoading) return;
    setDivAILoading(which);
    try {
      const body = {
        dob: form.dob,
        time: form.time,
        lat: city.lat,
        lon: city.lon,
        tz: city.tz,
        name: form.name || null,
        extra: { which },
      };
      const { data } = await guard(
        async () => await api.post("/astro/divisional-interpretation", body),
        "AI Divisional Interpretation"
      );
      setDivAI((prev) => ({ ...prev, [which]: data.interpretation }));
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "AI interpretation failed");
    } finally {
      setDivAILoading(null);
    }
  };

  // --- Share chart image ----
  const generateShareImage = async () => {
    if (!city || !form.dob) return;
    setShareLoading(true);
    try {
      const body = {
        dob: form.dob,
        time: form.time,
        lat: city.lat,
        lon: city.lon,
        tz: city.tz,
        name: form.name || null,
      };
      const { data } = await api.post("/astro/share-image", body);
      setShareImage(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Share image failed");
    } finally {
      setShareLoading(false);
    }
  };

  const shareOrDownload = async () => {
    if (!shareImage) return;
    // Try native Web Share API with file (works on mobile)
    try {
      const res = await fetch(shareImage.image);
      const blob = await res.blob();
      const file = new File([blob], shareImage.filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My Vedic Chart",
          text: shareImage.share_text,
          url: shareImage.share_url,
        });
        return;
      }
    } catch (_) { /* fall through to download */ }
    // Fallback: trigger a download
    const a = document.createElement("a");
    a.href = shareImage.image;
    a.download = shareImage.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-6" data-testid="birth-chart-page">
      <header>
        <h1 className="font-serif text-4xl text-slate-50 mb-2">{t("chart.title")}</h1>
        <p className="text-slate-400">{t("chart.subtitle")}</p>
      </header>

      <Card className="glass border-white/10 p-6">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="chart-form">
          <div>
            <Label className="text-slate-300">{t("auth.name")} <span className="text-slate-500 text-xs">(optional)</span></Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="chart-name-input"
              className="mt-1.5 bg-slate-900/50 border-white/10 text-white"
              placeholder="Your name"
            />
          </div>
          <div>
            <Label className="text-slate-300">{t("num.dob")}</Label>
            <div className="mt-1.5">
              <DatePicker
                value={form.dob}
                onChange={(v) => setForm({ ...form, dob: v })}
                testId="chart-dob-input"
                placeholder="Pick your date of birth"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-300">{t("chart.birth_time")}</Label>
            <Input
              type="time"
              required
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
              data-testid="chart-time-input"
              className="mt-1.5 bg-slate-900/50 border-white/10 text-white"
            />
          </div>
          <div>
            <Label className="text-slate-300">{t("chart.birth_place")}</Label>
            <div className="mt-1.5">
              <PlaceInput value={city} onChange={setCity} testId="chart-city-select" />
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <Button
              type="submit"
              disabled={loading}
              data-testid="chart-submit-btn"
              className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium px-8"
            >
              {loading ? t("chart.computing") : t("chart.generate")}
            </Button>
            {chart && (
              <Button
                type="button"
                onClick={downloadPdf}
                disabled={pdfLoading}
                data-testid="chart-pdf-btn"
                variant="outline"
                className="rounded-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200 gap-2"
              >
                <Download className="w-4 h-4" />
                {pdfLoading ? "Generating PDF..." : "Download PDF Report"}
              </Button>
            )}
            {chart && (
              <Button
                type="button"
                onClick={generateShareImage}
                disabled={shareLoading}
                data-testid="chart-share-btn"
                variant="outline"
                className="rounded-full border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 gap-2"
              >
                <Share2 className="w-4 h-4" />
                {shareLoading ? "Creating image..." : "Share Chart Image"}
              </Button>
            )}
            {error && <div className="w-full text-sm text-rose-400 mt-1" data-testid="chart-error">{error}</div>}
          </div>
        </form>
      </Card>

      {/* Share Chart Image preview modal */}
      {shareImage && (
        <div
          data-testid="share-image-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4"
        >
          <div className="relative w-full max-w-md rounded-3xl border border-amber-500/30 bg-slate-900 p-6 shadow-2xl">
            <button
              onClick={() => setShareImage(null)}
              data-testid="share-image-close"
              aria-label="Close"
              className="absolute right-4 top-4 text-slate-500 hover:text-slate-200"
            >
              ✕
            </button>
            <h3 className="font-serif text-xl text-slate-50 mb-3 text-center">Your Shareable Chart</h3>
            <img
              src={shareImage.image}
              alt="Your Vedic chart highlights"
              data-testid="share-image-preview"
              className="w-full rounded-2xl border border-white/10 mb-4"
            />
            <div className="flex gap-2">
              <Button
                onClick={shareOrDownload}
                data-testid="share-image-send"
                className="flex-1 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-600 gap-2"
              >
                <Share2 className="w-4 h-4" /> Share / Download
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-3 text-center">
              Perfect for Instagram, WhatsApp status, X — spread the cosmic vibes ✨
            </p>
          </div>
        </div>
      )}

      {chart && (
        <div className="space-y-5 animate-fade-up" data-testid="chart-result">
          {cachedAt && (
            <div className="flex justify-end">
              <OfflineBadge cachedAt={cachedAt} testId="chart-offline-badge" />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Card className="glass border-white/10 p-6">
              <div className="text-xs uppercase tracking-widest text-amber-300 mb-2">{t("chart.ascendant")}</div>
              <div className="font-serif text-3xl text-slate-50">{chart.ascendant.sign}</div>
              <div className="text-sm text-slate-400 mt-1">{chart.ascendant.degree}°</div>
            </Card>
            <Card className="glass border-white/10 p-6">
              <div className="text-xs uppercase tracking-widest text-amber-300 mb-2">{t("chart.moon_sign")}</div>
              <div className="font-serif text-3xl text-slate-50">{chart.moon_sign}</div>
              <div className="text-sm text-slate-400 mt-1">Nakshatra: {chart.moon_nakshatra}</div>
            </Card>
            <Card className="glass border-white/10 p-6">
              <div className="text-xs uppercase tracking-widest text-amber-300 mb-2">{t("chart.sun_sign")}</div>
              <div className="font-serif text-3xl text-slate-50">{chart.western_sun_sign}</div>
              <div className="text-sm text-slate-400 mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {chart.location.lat.toFixed(2)}, {chart.location.lon.toFixed(2)}
              </div>
            </Card>
          </div>

          {/* Planet table */}
          <Card className="glass border-white/10 p-6" data-testid="planet-table">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-gold" />
              <h3 className="font-serif text-2xl text-slate-50">{t("chart.planets")}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-amber-300 uppercase text-xs tracking-wider">
                    <th className="pb-3 pr-4">Planet</th>
                    <th className="pb-3 pr-4">Sign</th>
                    <th className="pb-3 pr-4">Degree</th>
                    <th className="pb-3 pr-4">House</th>
                    <th className="pb-3 pr-4">Nakshatra</th>
                    <th className="pb-3 pr-4">Lord</th>
                    <th className="pb-3 pr-4">Pada</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {Object.entries(chart.planets).map(([name, p]) => (
                    <tr key={name} className="text-slate-200">
                      <td className="py-3 pr-4 font-medium text-amber-300">{name}</td>
                      <td className="py-3 pr-4">{p.sign}</td>
                      <td className="py-3 pr-4">{p.degree}°</td>
                      <td className="py-3 pr-4">{p.house}</td>
                      <td className="py-3 pr-4">{p.nakshatra}</td>
                      <td className="py-3 pr-4 text-slate-400">{p.nakshatra_lord}</td>
                      <td className="py-3 pr-4 text-slate-400">{p.pada}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Divisional charts — tabs */}
          {chart.divisional && (
            <Card className="glass border-white/10 p-5" data-testid="divisional-section">
              <h3 className="font-serif text-2xl text-slate-50 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-gold" /> Divisional Charts (Vargas)
              </h3>
              <Tabs defaultValue="d9_navamsa">
                <TabsList className="bg-slate-900/50 border border-white/5 flex-wrap h-auto" data-testid="divisional-tabs">
                  {DIVISIONAL_TABS.map((d) => (
                    <TabsTrigger
                      key={d.key}
                      value={d.key}
                      data-testid={`tab-${d.key}`}
                      className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950 text-slate-300"
                    >
                      <span className="font-medium">{d.label}</span>
                      <span className="text-xs ml-1.5 opacity-70">· {d.desc}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>
                {DIVISIONAL_TABS.map((d) => (
                  <TabsContent key={d.key} value={d.key} className="mt-4" data-testid={`tab-content-${d.key}`}>
                    {chart.divisional[d.key] && (
                      <DivisionalCard
                        title={`${d.label} — ${d.desc}`}
                        data={chart.divisional[d.key]}
                        testId={`${d.key}-chart`}
                      />
                    )}
                    {["d9_navamsa", "d10_dasamsa"].includes(d.key) && (
                      <div className="mt-4" data-testid={`ai-interp-${d.key}`}>
                        {divAI[d.key] ? (
                          <div className="p-4 rounded-xl bg-indigo-500/5 border border-indigo-400/20">
                            <div className="flex items-center gap-2 mb-2 text-indigo-300 text-xs uppercase tracking-wider">
                              <Brain className="w-3.5 h-3.5" /> AI Interpretation
                            </div>
                            <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                              {divAI[d.key]}
                            </div>
                          </div>
                        ) : (
                          <Button
                            onClick={() => fetchDivisionalAI(d.key)}
                            disabled={!!divAILoading}
                            variant="outline"
                            data-testid={`ai-interp-btn-${d.key}`}
                            className="w-full rounded-full border-indigo-400/40 text-indigo-300 hover:bg-indigo-500/10"
                          >
                            {divAILoading === d.key ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Interpreting your {d.label}...</>
                            ) : (
                              <><Brain className="w-4 h-4 mr-2" /> Get AI Interpretation for {d.label}</>
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </Card>
          )}

          {/* Yogas detected */}
          {chart.yogas && chart.yogas.total > 0 && (
            <Card className="glass border-white/10 p-6" data-testid="yogas-section">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-gold" />
                <h3 className="font-serif text-2xl text-slate-50">Yogas in Your Chart</h3>
                <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300" data-testid="yogas-total">
                  {chart.yogas.total} detected
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ...chart.yogas.panch_mahapurush.map((y) => ({ ...y, category: "Panch Mahapurush" })),
                  ...chart.yogas.raj_yoga.map((y) => ({ ...y, category: "Raj Yoga" })),
                  ...chart.yogas.dhan_yoga.map((y) => ({ ...y, category: "Dhan Yoga" })),
                  ...chart.yogas.gaja_kesari.map((y) => ({ ...y, category: "Gaja Kesari" })),
                  ...chart.yogas.neecha_bhanga.map((y) => ({ ...y, category: "Neecha Bhanga" })),
                ].map((y, i) => (
                  <div
                    key={i}
                    className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-400/20"
                    data-testid={`yoga-card-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-wider text-emerald-300">
                      <Sparkles className="w-3.5 h-3.5" /> {y.category}
                    </div>
                    <div className="font-serif text-lg text-slate-50">{y.name}</div>
                    {(y.planet || (y.lords && y.lords.length > 0)) && (
                      <div className="text-xs text-amber-300 mt-0.5">
                        {y.planet || (y.lords || []).join(" + ")}
                      </div>
                    )}
                    {y.reason && (
                      <div className="text-xs text-slate-400 mt-1 italic">{y.reason}</div>
                    )}
                    <div className="text-sm text-slate-300 mt-2 leading-relaxed">{y.meaning}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Vimshottari Dasha */}
          {chart.dasha && (
            <Card className="glass border-white/10 p-6" data-testid="dasha-section">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-gold" />
                <h3 className="font-serif text-2xl text-slate-50">{t("chart.dasha")}</h3>
              </div>
              <div className="mb-5 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20" data-testid="current-dasha">
                <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">{t("chart.current_period")}</div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className="text-2xl font-serif text-slate-50">{chart.dasha.current_mahadasha || "—"}</div>
                  <span className="text-slate-400">Mahadasha</span>
                  <span className="text-slate-600">›</span>
                  <div className="text-xl font-serif text-amber-300">{chart.dasha.current_antardasha || "—"}</div>
                  <span className="text-slate-400">Antardasha</span>
                  {chart.dasha.current_pratyantar && (
                    <>
                      <span className="text-slate-600">›</span>
                      <div className="text-lg font-serif text-amber-200">{chart.dasha.current_pratyantar}</div>
                      <span className="text-slate-400 text-sm">Pratyantar</span>
                    </>
                  )}
                </div>
                <div className="text-sm text-slate-400 mt-2">
                  Birth nakshatra: <span className="text-slate-200">{chart.dasha.birth_nakshatra}</span>
                  {" · "}Lord: <span className="text-slate-200">{chart.dasha.birth_nakshatra_lord}</span>
                </div>
                {chart.dasha.current_prediction && (
                  <div className="mt-4 pt-4 border-t border-amber-500/20 space-y-2" data-testid="dasha-prediction">
                    <div className="text-sm text-slate-200 leading-relaxed">
                      {chart.dasha.current_prediction.headline}
                    </div>
                    <div className="text-xs text-emerald-300">
                      <span className="font-medium">✦ Likely events: </span>{chart.dasha.current_prediction.likely_events}
                    </div>
                    <div className="text-xs text-rose-300/90">
                      <span className="font-medium">⚠ Watch out: </span>{chart.dasha.current_prediction.watch_out_for}
                    </div>
                  </div>
                )}
              </div>

              {chart.dasha.upcoming_timeline && chart.dasha.upcoming_timeline.length > 0 && (
                <div className="mb-5" data-testid="dasha-upcoming-timeline">
                  <div className="text-xs uppercase tracking-wider text-amber-300 mb-2">Upcoming Life-Event Timeline</div>
                  <div className="space-y-2">
                    {chart.dasha.upcoming_timeline.map((e, i) => (
                      <div
                        key={i}
                        className={`p-3 rounded-lg border ${e.is_current ? "bg-amber-500/10 border-amber-500/30" : "bg-slate-900/40 border-white/5"}`}
                      >
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="text-sm">
                            <span className="font-medium text-slate-100">{e.mahadasha_lord}</span>
                            <span className="text-slate-500 mx-1">›</span>
                            <span className="text-amber-300">{e.antardasha_lord}</span>
                            {e.is_current && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/30 text-amber-200">NOW</span>}
                          </div>
                          <span className="text-xs text-slate-500">{e.start.slice(0, 10)} → {e.end.slice(0, 10)}</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                          <span className="text-emerald-300/80">✦ </span>{e.prediction.likely_events}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-xs uppercase tracking-wider text-amber-300 mb-2">Full Mahadasha Timeline</div>
              <div className="space-y-2">
                {chart.dasha.mahadashas.map((md, i) => {
                  const isCurrent = md.lord === chart.dasha.current_mahadasha;
                  return (
                    <div
                      key={i}
                      className={`p-3 rounded-lg border ${isCurrent ? "bg-amber-500/10 border-amber-500/30" : "bg-slate-900/40 border-white/5"}`}
                    >
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                          <TrendingUp className={`w-4 h-4 ${isCurrent ? "text-amber-300" : "text-slate-500"}`} />
                          <span className="font-medium text-slate-100">{md.lord}</span>
                          <span className="text-xs text-slate-500">{md.years} yrs</span>
                        </div>
                        <span className="text-xs text-slate-400">
                          {md.start.slice(0, 10)} → {md.end.slice(0, 10)}
                        </span>
                      </div>
                      {isCurrent && (
                        <div className="grid grid-cols-3 sm:grid-cols-9 gap-1 mt-3">
                          {md.antardashas.map((ad, j) => (
                            <div
                              key={j}
                              className={`text-xs text-center px-1 py-1 rounded ${
                                ad.lord === chart.dasha.current_antardasha
                                  ? "bg-amber-500/30 text-amber-200 font-medium"
                                  : "bg-slate-900/60 text-slate-400"
                              }`}
                              title={`${ad.start.slice(0, 10)} → ${ad.end.slice(0, 10)}`}
                            >
                              {ad.lord.slice(0, 3)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
          {/* Sade Sati */}
          {chart.sade_sati && (
            <Card className="glass border-white/10 p-6" data-testid="sade-sati-section">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className={`w-5 h-5 ${chart.sade_sati.status === "active" ? "text-rose-400" : "text-emerald-400"}`} />
                <h3 className="font-serif text-2xl text-slate-50">Sade Sati Status</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-slate-900/40 border border-white/5">
                  <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">Phase</div>
                  <div className="text-lg font-medium text-slate-50">{chart.sade_sati.phase}</div>
                  <div className={`text-xs mt-1 ${chart.sade_sati.status === "active" ? "text-rose-300" : "text-emerald-300"}`}>
                    Intensity: {chart.sade_sati.intensity}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-slate-900/40 border border-white/5">
                  <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">Current Saturn</div>
                  <div className="text-lg font-medium text-slate-50">{chart.sade_sati.current_saturn_sign}</div>
                  <div className="text-xs text-slate-400 mt-1">{chart.sade_sati.current_saturn_degree}°</div>
                </div>
                <div className="p-4 rounded-xl bg-slate-900/40 border border-white/5">
                  <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">Natal Moon</div>
                  <div className="text-lg font-medium text-slate-50">{chart.sade_sati.natal_moon_sign}</div>
                  {chart.sade_sati.approx_exit_date && (
                    <div className="text-xs text-slate-400 mt-1">
                      Ends ~{chart.sade_sati.approx_exit_date.slice(0, 10)}
                    </div>
                  )}
                </div>
              </div>
              {chart.sade_sati.remedies.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-wider text-amber-300 mb-2">Suggested Remedies</div>
                  <ul className="space-y-1 text-sm text-slate-300">
                    {chart.sade_sati.remedies.map((r, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-gold mt-0.5">•</span>
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          )}

          {/* KP Chart Snapshot */}
          {chart.kp && (
            <Card className="glass border-white/10 p-6" data-testid="kp-section">
              <h3 className="font-serif text-2xl text-slate-50 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-gold" /> KP Astrology — Sub-Lords
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-amber-300 uppercase text-xs tracking-wider">
                      <th className="pb-3 pr-4">Planet/Body</th>
                      <th className="pb-3 pr-4">Sign</th>
                      <th className="pb-3 pr-4">Nakshatra (Star Lord)</th>
                      <th className="pb-3 pr-4">Sub-Lord</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    <tr className="text-slate-200">
                      <td className="py-3 pr-4 font-medium text-amber-300">Ascendant</td>
                      <td className="py-3 pr-4">{chart.kp.ascendant.sign}</td>
                      <td className="py-3 pr-4">{chart.kp.ascendant.nakshatra} <span className="text-slate-500 text-xs">({chart.kp.ascendant.nakshatra_lord})</span></td>
                      <td className="py-3 pr-4 text-amber-300">{chart.kp.ascendant.sub_lord}</td>
                    </tr>
                    {Object.entries(chart.kp.planets).map(([name, p]) => (
                      <tr key={name} className="text-slate-200">
                        <td className="py-3 pr-4 font-medium text-amber-300">{name}</td>
                        <td className="py-3 pr-4">{p.sign}</td>
                        <td className="py-3 pr-4">{p.nakshatra} <span className="text-slate-500 text-xs">({p.nakshatra_lord})</span></td>
                        <td className="py-3 pr-4 text-amber-300">{p.sub_lord}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                KP (Krishnamurti Paddhati) uses sub-lord placements to refine predictive accuracy.
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function DivisionalCard({ title, data, testId }) {
  // Build 12-house grid showing which planets are in each sign
  const planetsBySign = {};
  Object.entries(data.planets).forEach(([name, p]) => {
    if (!planetsBySign[p.sign_index]) planetsBySign[p.sign_index] = [];
    planetsBySign[p.sign_index].push(name);
  });

  return (
    <Card className="glass border-white/10 p-5" data-testid={testId}>
      <h4 className="font-serif text-lg text-slate-50 mb-3">{title}</h4>
      <div className="text-xs text-amber-300 mb-3">Ascendant: <span className="text-slate-100">{data.ascendant.sign}</span></div>
      <div className="grid grid-cols-4 grid-rows-3 gap-1.5 aspect-[4/3]">
        {[...Array(12)].map((_, i) => {
          // North Indian style index mapping
          const positions = [
            { idx: 0, gridArea: "1 / 2" }, { idx: 1, gridArea: "1 / 1" }, { idx: 2, gridArea: "2 / 1" },
            { idx: 3, gridArea: "3 / 1" }, { idx: 4, gridArea: "3 / 2" }, { idx: 5, gridArea: "3 / 3" },
            { idx: 6, gridArea: "3 / 4" }, { idx: 7, gridArea: "2 / 4" }, { idx: 8, gridArea: "1 / 4" },
            { idx: 9, gridArea: "1 / 3" }, { idx: 10, gridArea: "2 / 3" }, { idx: 11, gridArea: "2 / 2" },
          ];
          const pos = positions[i];
          const signIdx = pos.idx;
          const planets = planetsBySign[signIdx] || [];
          const isAsc = signIdx === data.ascendant.sign_index;
          return (
            <div
              key={i}
              style={{ gridArea: pos.gridArea }}
              className={`rounded p-1.5 text-[10px] flex flex-col gap-0.5 ${
                isAsc ? "bg-amber-500/20 border border-amber-500/40" : "bg-slate-900/50 border border-white/5"
              }`}
            >
              <div className="text-amber-300 text-[10px] font-medium">{RASI_SHORT[signIdx]}</div>
              <div className="flex flex-wrap gap-0.5">
                {planets.map((pl) => (
                  <span key={pl} className="text-slate-200 px-1 py-0.5 rounded bg-white/5 text-[9px]">
                    {pl.slice(0, 2)}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
